import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { type Plan } from '../types'
import { PlanCard } from './PlanCard'
import { PlansTabs } from './PlansTabs'

// Re-export de los sub-componentes para uso externo directo.
// Ej: importar PlanCard en un dashboard sin pasar por PlansSection.
export { PlanCard } from './PlanCard'
export { PlansTabs } from './PlansTabs'

interface PlansSectionProps {
  plans: Plan[]
  title?: string
  description?: string
}

/**
 * Sección de planes reutilizable para landings de marketing.
 *
 * Comportamiento según número de tiers:
 *  - 1 tier (ej. Counter-Strike con 2 planes del mismo tier):
 *      muestra todos los planes en grid, sin tabs.
 *  - 2+ tiers (ej. Minecraft con Básico/Intermedio/Élite):
 *      tabs clickeables arriba, solo se ven los planes del tier activo.
 *
 * Sub-componentes exportables: `PlanCard` y `PlansTabs` se pueden
 * importar directamente para uso fuera de una sección completa.
 *
 * Ejemplo de uso fuera de esta sección:
 *   import { PlanCard } from './marketing/components/PlanCard'
 *   <PlanCard plan={userPlan} delayMultiplier={0} />
 */
export function PlansSection({
  plans,
  title = 'Elige tu plan',
  description = 'Pagas solo por las horas que el servidor esté encendido.',
}: PlansSectionProps) {
  if (plans.length === 0) {
    return (
      <section className="plans-section">
        <div className="plans-section-header">
          <span className="plans-section-tag">PLANES</span>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <p className="plans-empty">
          Los precios no están disponibles en este momento. Vuelve a intentarlo en unos segundos.
        </p>
      </section>
    )
  }

  // Agrupar planes por label manteniendo el orden de aparición
  const tiers = new Map<string, Plan[]>()
  for (const plan of plans) {
    const list = tiers.get(plan.label) ?? []
    list.push(plan)
    tiers.set(plan.label, list)
  }
  const grouped = Array.from(tiers.entries())
  const tierNames = grouped.map(([name]) => name)

  // Si hay 1 tier no hay tabs (más limpio, sin chrome inútil)
  if (grouped.length === 1) {
    return (
      <section className="plans-section">
        <div className="plans-section-header">
          <span className="plans-section-tag">PLANES</span>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <div className="plans-grid">
          {plans.map((plan, idx) => (
            <PlanCard key={plan.id + '-' + plan.location} plan={plan} delayMultiplier={idx * 0.06} />
          ))}
        </div>
        <p className="plans-footnote">
          ¿Necesitas más RAM, SSD o vCPUs? Contáctanos y configuramos un plan a medida.
        </p>
      </section>
    )
  }

  // 2+ tiers: renderizar tabs + contenido del tier activo
  return <MultiTierPlans title={title} description={description} grouped={grouped} tierNames={tierNames} />
}

interface MultiTierPlansProps {
  title: string
  description: string
  grouped: Array<[string, Plan[]]>
  tierNames: string[]
}

function MultiTierPlans({ title, description, grouped, tierNames }: MultiTierPlansProps) {
  const [activeTier, setActiveTier] = useState(tierNames[0])
  const activePlans = grouped.find(([name]) => name === activeTier)?.[1] ?? []

  return (
    <section className="plans-section">
      <div className="plans-section-header">
        <span className="plans-section-tag">PLANES</span>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      <PlansTabs tiers={tierNames} active={activeTier} onChange={setActiveTier} />

      {/* Cards del tier activo con animación de transición */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTier}
          className="plans-grid"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.25 }}
        >
          {activePlans.map((plan, idx) => (
            <PlanCard
              key={plan.id + '-' + plan.location}
              plan={plan}
              delayMultiplier={idx * 0.06}
            />
          ))}
        </motion.div>
      </AnimatePresence>

      <p className="plans-footnote">
        ¿Necesitas más RAM, SSD o vCPUs? Contáctanos y configuramos un plan a medida.
      </p>
    </section>
  )
}